import React, { use } from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import CardTravelRounded from '@mui/icons-material/CardTravelRounded';
import SearchOffRoundedIcon from '@mui/icons-material/SearchOffRounded';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import CoffeeIcon from 'assets/icons/coffee.svg?react';
import LoadingIndicator from 'shared/components/loading-indicator/loading-indicator';
import Permission from 'shared/features/auth/authorization/enums/permission';
import usePermissionAuthorization from 'shared/features/auth/authorization/hooks/permission-authorization';
import { formatDateTimeWithoutYear } from 'shared/utils/date-utils';

import ElectronicClientRecordClientResultOption from 'features/residents/models/electronic-client-record-client-result-option';
import { MuteScenariosReason } from 'features/residents/models/mute-scenarios-reason';
import ResidentSettingDetailsHeader from 'features/residents/resident-settings/components/resident-setting-details-header/resident-setting-details-header';
import ResidentSettingDetailsInformationAccordion from 'features/residents/resident-settings/components/resident-setting-details-information-accordion/resident-setting-details-information-accordion';
import ResidentSettingDetailsMuteScenariosAccordion from 'features/residents/resident-settings/components/resident-setting-details-mute-scenarios-accordion/resident-setting-details-mute-scenarios-accordion';
import ResidentSettingDetailsScenariosAccordion from 'features/residents/resident-settings/components/resident-setting-details-scenarios-accordion/resident-setting-details-scenarios-accordion';
import DevicesAccordion from 'features/residents/resident-settings/components/resident-setting-details/components/devices-accordion/devices-accordion';
import ElectronicClientRecordAlertAccordion from 'features/residents/resident-settings/components/resident-setting-details/components/electronic-client-record-alert-accordion/electronic-client-record-alert-accordion';
import ResuscitationAlert from 'features/residents/resident-settings/components/resident-setting-details/components/resuscitation-alert/resuscitation-alert';
import useResidentSettingDetails from 'features/residents/resident-settings/components/resident-setting-details/hooks/use-resident-setting-details';
import ResidentSettingsProviderContext from 'features/residents/resident-settings/providers/contexts/resident-settings-provider-context';
import styles from './resident-setting-details.module.scss';

const ResidentSettingDetails = (): React.JSX.Element => {
  const { t } = useTranslation('residentsSettings');
  const { hasPermission } = usePermissionAuthorization();

  const {
    detailEditState: { residentSettingEditForm, viewingMode, currentResidentSetting },
  } = use(ResidentSettingsProviderContext);

  const {
    formState: { errors },
  } = residentSettingEditForm;

  const hasErrors = errors && Object.keys(errors).length > 0;

  const { isScrolling, isSubmitting, updateIsLoading, submitForm, handleScroll } = useResidentSettingDetails();
  const ecd = currentResidentSetting?.electronicClientRecordClientResult;

  return (
    <div className={styles.residentDetails}>
      <FormProvider {...residentSettingEditForm}>
        <form onSubmit={residentSettingEditForm.handleSubmit(submitForm)}>
          <ResidentSettingDetailsHeader
            isScrolling={isScrolling}
            isSubmitting={isSubmitting}
            isLoading={updateIsLoading}
            hasErrors={hasErrors}
          />
          <div className={`${styles.body} ${viewingMode === 'viewing' && 'is-viewing'}`} onScroll={handleScroll}>
            {updateIsLoading ? (
              <div className="organisation-settings-loader">
                <LoadingIndicator />
              </div>
            ) : (
              <>
                {ecd?.result === ElectronicClientRecordClientResultOption.Failed && (
                  <Alert icon={<SearchOffRoundedIcon className={styles.ecdWarningIcon} />} className={styles.ecdWarning}>
                    {t('electronicClientRecordWarning')}
                  </Alert>
                )}
                <ResuscitationAlert resuscitationDecisionType={ecd?.clients?.[0]?.resuscitationDecisionType} />

                {currentResidentSetting?.scenariosMuted && currentResidentSetting.muteScenariosUntil && (
                  <div className={styles.muteScenariosBanner}>
                    {currentResidentSetting.muteScenariosReason === MuteScenariosReason.Visitor ? (
                      <CoffeeIcon className={styles.muteScenariosBannerIcon} />
                    ) : (
                      <CardTravelRounded />
                    )}
                    <Typography variant="body1">{t(`muteScenarios.title.${currentResidentSetting.muteScenariosReason}`)}</Typography>
                    <Typography variant="body2" sx={{ marginLeft: 'auto' }}>
                      {t('muteScenarios.until')} {formatDateTimeWithoutYear(currentResidentSetting.muteScenariosUntil)}
                    </Typography>
                  </div>
                )}

                {ecd?.clients && ecd?.clients?.[0]?.alerts?.length > 0 && <ElectronicClientRecordAlertAccordion />}

                <ResidentSettingDetailsInformationAccordion />

                <ResidentSettingDetailsScenariosAccordion />

                {hasPermission(Permission.UpdateResidentAbsence) && (
                  <ResidentSettingDetailsMuteScenariosAccordion key={currentResidentSetting?.organisationUnitId} />
                )}

                <DevicesAccordion />
              </>
            )}
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default ResidentSettingDetails;
